import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import {
  ContextType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Virtuoso } from 'react-virtuoso';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { JobInvoicing } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import JobsQueryContext, {
  JobsQueryContextProvider,
} from '~pages/Approvals/Files/components/OrdersList/ExpandedBlock/JobsQueryContext';
import {
  AllOrdersLoadedIndicator,
  VirtualizedOrdersLoadingIndicator,
} from '~pages/Approvals/Files/components/OrdersList/OrdersList';
import { makeJobsQueryContextKey } from '~pages/Approvals/Files/utils';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { ChargesBillingLinesQueryContextProvider } from '../ChargesBillingLinesQueryContext';
import { chargesListCardSx } from '../chargesGrid';
import {
  ChargesGroup,
  ChargesGroupBy,
  ChargesGroupOrder,
  groupChargesJobs,
} from '../chargesGrouping';
import ChargesGroupHeader from './ChargesGroupHeader';
import ChargesJobsHeader from './ChargesJobsHeader';
import { OrderJobRows } from './ChargesOrderSection';

const UNCLIPPED_EDGE = '-100%';
const CLIPPED_EDGE = '0';

type JobsQueryContextValue = ContextType<typeof JobsQueryContext>;

type GroupedListRow =
  | { group: ChargesGroup; isOpen: boolean; kind: 'group'; totalsJobIds: string[] }
  | {
      groupKey: string;
      groupOrder: ChargesGroupOrder;
      isLastOfGroup: boolean;
      jobsQueryContextValue: JobsQueryContextValue;
      kind: 'order';
    };

interface ChargesGroupedListProps {
  groupBy: Exclude<ChargesGroupBy, ChargesGroupBy.NONE>;
  hasMore: boolean;
  hasUnfilteredResults: boolean;
  isLoading: boolean;
  onClearFilters: () => void;
  onLoadMore: () => void;
  orders: Order[];
}

export default function ChargesGroupedList({
  groupBy,
  hasMore,
  hasUnfilteredResults,
  isLoading,
  onClearFilters,
  onLoadMore,
  orders,
}: ChargesGroupedListProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { jobsQuery } = useContext(ApproveChargesPageContext);
  const queryClient = useQueryClient();
  const [jobsReadByOrderId, setJobsReadByOrderId] = useState<Map<string, OrderJobsRead>>(
    new Map(),
  );

  const reportOrderJobs = useCallback(
    (orderId: string, jobsRead: OrderJobsRead | undefined) => {
      setJobsReadByOrderId((state) => {
        const nextState = new Map(state);

        if (jobsRead) {
          nextState.set(orderId, jobsRead);
        } else {
          nextState.delete(orderId);
        }

        return nextState;
      });
    },
    [],
  );

  const groups = useMemo(() => {
    const ordersWithJobs = orders.flatMap((order) => {
      const jobsRead = jobsReadByOrderId.get(order.id);

      return jobsRead && !jobsRead.isFailed ? [{ jobs: jobsRead.jobs, order }] : [];
    });

    return groupChargesJobs(ordersWithJobs, groupBy);
  }, [groupBy, jobsReadByOrderId, orders]);

  const [closedGroupKeys, setClosedGroupKeys] = useState<ReadonlySet<string>>(new Set());

  const toggleGroup = useCallback((groupKey: string) => {
    setClosedGroupKeys((keys) => {
      const nextKeys = new Set(keys);

      if (nextKeys.has(groupKey)) {
        nextKeys.delete(groupKey);
      } else {
        nextKeys.add(groupKey);
      }

      return nextKeys;
    });
  }, []);

  const isReadingJobs = orders.some((order) => !jobsReadByOrderId.has(order.id));
  const [settledJobIdsByGroupKey, setSettledJobIdsByGroupKey] = useState<
    ReadonlyMap<string, string[]>
  >(new Map());

  useEffect(() => {
    if (!isReadingJobs) {
      setSettledJobIdsByGroupKey(
        new Map(groups.map((group) => [group.key, group.jobIds])),
      );
    }
  }, [groups, isReadingJobs]);

  const rows = useMemo(() => {
    const listRows: GroupedListRow[] = [];

    groups.forEach((group) => {
      const isOpen = !closedGroupKeys.has(group.key);
      const totalsJobIds =
        settledJobIdsByGroupKey.get(group.key) ?? (isReadingJobs ? [] : group.jobIds);
      listRows.push({ group, isOpen, kind: 'group', totalsJobIds });

      if (isOpen) {
        group.orders.forEach((groupOrder, index) => {
          const jobsRead = jobsReadByOrderId.get(groupOrder.order.id);

          if (jobsRead?.isFailed === false) {
            listRows.push({
              groupKey: group.key,
              groupOrder,
              isLastOfGroup: index === group.orders.length - 1,
              jobsQueryContextValue: jobsRead.jobsQueryContextValue,
              kind: 'order',
            });
          }
        });
      }
    });

    return listRows;
  }, [
    closedGroupKeys,
    groups,
    isReadingJobs,
    jobsReadByOrderId,
    settledJobIdsByGroupKey,
  ]);

  const renderRow = useCallback(
    (_: number, row: GroupedListRow) =>
      row.kind === 'group' ? (
        <ChargesGroupHeaderRow
          group={row.group}
          isOpen={row.isOpen}
          onToggle={toggleGroup}
          totalsJobIds={row.totalsJobIds}
        />
      ) : (
        <ChargesGroupOrderRow
          companyId={companyId}
          groupOrder={row.groupOrder}
          isLastOfGroup={row.isLastOfGroup}
          jobsQueryContextValue={row.jobsQueryContextValue}
        />
      ),
    [companyId, toggleGroup],
  );

  const ordersWithFailedJobs = useMemo(
    () => orders.filter((order) => jobsReadByOrderId.get(order.id)?.isFailed),
    [jobsReadByOrderId, orders],
  );

  const retryFailedJobs = useCallback(() => {
    ordersWithFailedJobs.forEach((order) => {
      void queryClient.refetchQueries({
        queryKey: [makeJobsQueryContextKey(order.id)],
      });
    });
  }, [ordersWithFailedJobs, queryClient]);

  const [isAtBottom, setIsAtBottom] = useState(false);
  const [orderCountAtLastLoadMore, setOrderCountAtLastLoadMore] = useState<number | null>(
    null,
  );
  const isWaitingToLoadMore =
    isAtBottom && hasMore && orderCountAtLastLoadMore !== orders.length;

  const handleAtBottomChange = useCallback((isAtBottomNow: boolean) => {
    setIsAtBottom(isAtBottomNow);

    if (!isAtBottomNow) {
      setOrderCountAtLastLoadMore(null);
    }
  }, []);

  const canAskForMoreOrders = isWaitingToLoadMore && !isLoading && !isReadingJobs;

  useEffect(() => {
    if (canAskForMoreOrders) {
      setOrderCountAtLastLoadMore(orders.length);
      onLoadMore();
    }
  }, [canAskForMoreOrders, onLoadMore, orders.length]);

  const isLoadingRows = isLoading || isReadingJobs || isWaitingToLoadMore;
  const footerContext = useMemo(
    () => ({
      failedOrderCount: ordersWithFailedJobs.length,
      hasMore,
      hasOrders: orders.length > 0,
      isLoadingRows,
      onClearFilters,
      onRetryFailedJobs: retryFailedJobs,
      shouldShowClearFiltersHint: orders.length === 0 && hasUnfilteredResults,
    }),
    [
      hasMore,
      hasUnfilteredResults,
      isLoadingRows,
      onClearFilters,
      orders.length,
      ordersWithFailedJobs.length,
      retryFailedJobs,
    ],
  );

  return (
    <>
      {orders.map((order) => (
        <JobsQueryContextProvider
          key={order.id}
          companyId={companyId}
          extraJobsQuery={jobsQuery}
          order={order}
          shouldFetchOnMount
        >
          <OrderJobsReporter onJobs={reportOrderJobs} orderId={order.id} />
        </JobsQueryContextProvider>
      ))}

      <Virtuoso
        atBottomStateChange={handleAtBottomChange}
        components={GROUPED_LIST_COMPONENTS}
        computeItemKey={computeRowKey}
        context={footerContext}
        data={rows}
        itemContent={renderRow}
        overscan={5}
        useWindowScroll
      />
    </>
  );
}

const computeRowKey = (_: number, row: GroupedListRow) =>
  row.kind === 'group' ? row.group.key : `${row.groupKey}:${row.groupOrder.order.id}`;

interface GroupedListFooterContext {
  failedOrderCount: number;
  hasMore: boolean;
  hasOrders: boolean;
  isLoadingRows: boolean;
  onClearFilters: () => void;
  onRetryFailedJobs: () => void;
  shouldShowClearFiltersHint: boolean;
}

function GroupedListFooter({ context }: { context: GroupedListFooterContext }) {
  if (context.isLoadingRows) {
    return <VirtualizedOrdersLoadingIndicator />;
  }

  if (context.failedOrderCount > 0) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          py: 1.5,
        }}
      >
        <Typography sx={{ color: 'error.main' }} variant="body2">
          {t('approvals.approve_charges.groups.jobs_read_failed', {
            count: context.failedOrderCount,
          })}
        </Typography>
        <SmallButton onClick={context.onRetryFailedJobs} variant="outlined">
          {t('actions.retry')}
        </SmallButton>
      </Box>
    );
  }

  if (context.hasMore) {
    return null;
  }

  return (
    <AllOrdersLoadedIndicator
      hasOrders={context.hasOrders}
      onClearFilters={context.onClearFilters}
      showClearFiltersHint={context.shouldShowClearFiltersHint}
    />
  );
}

const GROUPED_LIST_COMPONENTS = { Footer: GroupedListFooter };

type OrderJobsRead =
  | {
      isFailed: false;
      jobs: JobInvoicing[];
      jobsQueryContextValue: JobsQueryContextValue;
    }
  | { isFailed: true };

interface OrderJobsReporterProps {
  onJobs: (orderId: string, jobsRead: OrderJobsRead | undefined) => void;
  orderId: string;
}

function OrderJobsReporter({ onJobs, orderId }: OrderJobsReporterProps) {
  const {
    clearQueryData,
    data: jobs,
    fetch,
    isError,
    order,
    updateJobInQueryData,
  } = useContext(JobsQueryContext);

  useEffect(() => {
    if (jobs) {
      onJobs(orderId, {
        isFailed: false,
        jobs,
        jobsQueryContextValue: {
          clearQueryData,
          data: jobs,
          fetch,
          isError,
          order,
          updateJobInQueryData,
        },
      });
    } else {
      onJobs(orderId, isError ? { isFailed: true } : undefined);
    }
  }, [
    clearQueryData,
    fetch,
    isError,
    jobs,
    onJobs,
    order,
    orderId,
    updateJobInQueryData,
  ]);

  return null;
}

interface GroupCardSlice {
  isFirstSlice: boolean;
  isLastSlice: boolean;
}

const groupCardSliceSx = (
  theme: Theme,
  { isFirstSlice, isLastSlice }: GroupCardSlice,
) => {
  const topCornerRadius = isFirstSlice ? theme.spacing() : '0px';
  const bottomCornerRadius = isLastSlice ? theme.spacing() : '0px';
  const topEdge = isFirstSlice ? UNCLIPPED_EDGE : CLIPPED_EDGE;
  const bottomEdge = isLastSlice ? UNCLIPPED_EDGE : CLIPPED_EDGE;

  return {
    ...chargesListCardSx(theme),
    borderRadius: `${topCornerRadius} ${topCornerRadius} ${bottomCornerRadius} ${bottomCornerRadius}`,
    borderWidth: `${isFirstSlice ? 1 : 0}px 1px ${isLastSlice ? 1 : 0}px`,
    clipPath: `inset(${topEdge} ${UNCLIPPED_EDGE} ${bottomEdge})`,
  };
};

interface ChargesGroupHeaderRowProps {
  group: ChargesGroup;
  isOpen: boolean;
  onToggle: (groupKey: string) => void;
  totalsJobIds: string[];
}

function ChargesGroupHeaderRow({
  group,
  isOpen,
  onToggle,
  totalsJobIds,
}: ChargesGroupHeaderRowProps) {
  return (
    <Box sx={{ pb: isOpen ? 0 : 1.5 }}>
      <Box
        sx={(theme) =>
          groupCardSliceSx(theme, { isFirstSlice: true, isLastSlice: !isOpen })
        }
      >
        <ChargesGroupHeader
          group={group}
          isOpen={isOpen}
          onToggle={() => onToggle(group.key)}
          totalsJobIds={totalsJobIds}
        />
        {isOpen && <ChargesJobsHeader />}
      </Box>
    </Box>
  );
}

interface ChargesGroupOrderRowProps {
  companyId: string;
  groupOrder: ChargesGroupOrder;
  isLastOfGroup: boolean;
  jobsQueryContextValue: JobsQueryContextValue;
}

function ChargesGroupOrderRow({
  companyId,
  groupOrder: { jobs, order },
  isLastOfGroup,
  jobsQueryContextValue,
}: ChargesGroupOrderRowProps) {
  const joinedJobIds = jobs.map((job) => job.id).join(',');
  const jobIds = useMemo(() => joinedJobIds.split(','), [joinedJobIds]);

  return (
    <Box sx={{ pb: isLastOfGroup ? 1.5 : 0 }}>
      <Box
        sx={(theme) =>
          groupCardSliceSx(theme, { isFirstSlice: false, isLastSlice: isLastOfGroup })
        }
      >
        <JobsQueryContext.Provider value={jobsQueryContextValue}>
          <ChargesBillingLinesQueryContextProvider
            companyId={companyId}
            jobIds={jobIds}
            orderId={order.id}
          >
            <OrderJobRows jobs={jobs} />
          </ChargesBillingLinesQueryContextProvider>
        </JobsQueryContext.Provider>
      </Box>
    </Box>
  );
}
